import { ApolloError } from '@apollo/client';
import { useSaveEnglishProficiencyResponseMutation } from 'generated/graphql';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TEnglishProficiencyAnswer } from './EnglishProficiencyTest.helpers';

/** Quiet period before a typed answer is sent. Long enough that a word being
 * spelled out is one save rather than six. */
export const SAVE_DEBOUNCE_MS = 900;
/** How often a queue held up by a dropped connection tries again. */
export const SAVE_RETRY_INTERVAL_MS = 5_000;

type HookProps = {
  /** The qcalc sitting's id, not the stage action candidate's. */
  assessmentId: string;
  /** The clock, as the server reports it on every save. */
  onRemainingSeconds: (remainingSeconds: number) => void;
  /** A save the server refused. Retrying will not help; tell the candidate. */
  onRejected: (message: string) => void;
};

export type EnglishProficiencyResponsesHandle = {
  /**
   * Records one answer. `immediate` sends it straight away — a chosen option
   * is a finished thought, a half-typed word is not.
   */
  save: (
    questionIndex: number,
    answer: TEnglishProficiencyAnswer,
    options?: { immediate?: boolean }
  ) => void;
  /** How many answers are waiting to reach the server. */
  pendingCount: number;
  /** True once a save has failed and the queue is waiting to try again. */
  isOffline: boolean;
  /**
   * Drains the queue and resolves when it is empty or has stopped making
   * progress. Auto-submit awaits this: an answer chosen at the deadline is
   * still crossing the network when the clock stops, and the server's ten
   * seconds of grace make that survivable, not guaranteed.
   */
  flush: () => Promise<void>;
};

/**
 * The write side of a sitting: one answer per mutation, queued so the newest
 * answer to a question replaces any older one still waiting, and re-queued
 * rather than dropped when the network is gone.
 *
 * Saving never ends the sitting. Only the candidate's own submit and the clock
 * do that, so nothing here can take the test away mid-answer.
 *
 * Modelled on `useIntegrityMonitoring`, the app's other disconnect-tolerant
 * writer. It differs in one way that matters: an integrity event is worth
 * losing, an answer is not, so nothing is ever discarded on a network error.
 */
export const useEnglishProficiencyResponses = ({
  assessmentId,
  onRemainingSeconds,
  onRejected,
}: HookProps): EnglishProficiencyResponsesHandle => {
  const [saveResponse] = useSaveEnglishProficiencyResponseMutation();

  // Latest pending answer per question. A Map because insertion order is the
  // send order, and re-setting a key keeps its place in the queue.
  const queueRef = useRef<Map<number, TEnglishProficiencyAnswer>>(new Map());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const drainingRef = useRef<Promise<void> | null>(null);

  const [pendingCount, setPendingCount] = useState(0);
  const [isOffline, setIsOffline] = useState(false);

  // The callbacks change identity on every parent render; hold them in refs so
  // the drain loop is stable and never restarts mid-flight.
  const callbacksRef = useRef({ onRemainingSeconds, onRejected });
  useEffect(() => {
    callbacksRef.current = { onRemainingSeconds, onRejected };
  }, [onRemainingSeconds, onRejected]);

  const syncPendingCount = useCallback(() => {
    setPendingCount(queueRef.current.size);
  }, []);

  const drain = useCallback((): Promise<void> => {
    if (drainingRef.current) {
      return drainingRef.current;
    }

    const run = async (): Promise<void> => {
      while (queueRef.current.size > 0) {
        const [questionIndex, answer] = queueRef.current.entries().next()
          .value as [number, TEnglishProficiencyAnswer];
        queueRef.current.delete(questionIndex);
        syncPendingCount();

        try {
          const { data } = await saveResponse({
            variables: {
              id: assessmentId,
              questionIndex,
              selectedAnswerIndex: answer.selectedAnswerIndex,
              blankAnswers: answer.blankAnswers,
            },
          });
          setIsOffline(false);

          const saved = data?.saveEnglishProficiencyResponse;
          if (!saved) {
            continue;
          }
          callbacksRef.current.onRemainingSeconds(saved.remainingSeconds);
        } catch (error) {
          const apolloError = error as ApolloError;
          const wasRefused = (apolloError?.graphQLErrors?.length ?? 0) > 0;

          if (wasRefused) {
            // The server rejected this answer — an expired clock, a submitted
            // sitting, an answer of the wrong shape. Sending it again would
            // be refused the same way, so drop it and say so.
            callbacksRef.current.onRejected(apolloError.message);
            continue;
          }

          // A network fault. Put the answer back unless the candidate has
          // since changed it, and leave the rest of the queue for the retry.
          if (!queueRef.current.has(questionIndex)) {
            const requeued = new Map<number, TEnglishProficiencyAnswer>([
              [questionIndex, answer],
            ]);
            queueRef.current.forEach((queuedAnswer, queuedIndex) =>
              requeued.set(queuedIndex, queuedAnswer)
            );
            queueRef.current = requeued;
          }
          syncPendingCount();
          setIsOffline(true);
          return;
        }
      }
    };

    const drainPromise = run().finally(() => {
      drainingRef.current = null;
    });
    drainingRef.current = drainPromise;
    return drainPromise;
  }, [assessmentId, saveResponse, syncPendingCount]);

  const save = useCallback<EnglishProficiencyResponsesHandle['save']>(
    (questionIndex, answer, options) => {
      queueRef.current.set(questionIndex, answer);
      syncPendingCount();

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (options?.immediate) {
        void drain();
        return;
      }
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        void drain();
      }, SAVE_DEBOUNCE_MS);
    },
    [drain, syncPendingCount]
  );

  const flush = useCallback(async (): Promise<void> => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    // Twice: the first pass sends whatever is queued, the second picks up an
    // answer that arrived while it was in flight.
    await drain();
    if (queueRef.current.size > 0) {
      await drain();
    }
  }, [drain]);

  // Anything the network held up is tried again on a timer, so a candidate who
  // drops off wifi mid-answer does not have to touch the question again.
  useEffect(() => {
    const retry = setInterval(() => {
      if (queueRef.current.size > 0) {
        void drain();
      }
    }, SAVE_RETRY_INTERVAL_MS);
    return () => clearInterval(retry);
  }, [drain]);

  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    []
  );

  return { save, pendingCount, isOffline, flush };
};
